import { supabase, supabaseAdmin, hasValidCredentials } from './supabase'
import type { Database } from './supabase'

// Type aliases
type CelebrityDb = Database['public']['Tables']['celebrities']['Row']
type AgentDb = Database['public']['Tables']['agents']['Row']

// Frontend-compatible types
export interface GuestAgent {
  id: string
  agentId: string
  name: string
  agency: string
  email: string
  phone: string
  isPrimary: boolean
}

export interface Guest {
  id: string
  name: string
  category: string[]
  days: string[]
  knownFor: string
  agents: GuestAgent[]
  prAvailable: string
  priority: 'A' | 'B' | 'C' | 'Unassigned'
  storyDetails?: string
  notes?: string
  source: string
}

export interface Agent {
  id: string
  name: string
  agency: string
  email: string
  phone: string
  eventYear: number
  totalRequests: number
  source: string
}

// Conversion functions
const convertGuest = (dbGuest: any): Guest => ({
  id: dbGuest.id,
  name: dbGuest.name,
  category: dbGuest.category,
  days: dbGuest.days,
  knownFor: dbGuest.known_for,
  agents: (dbGuest.celebrity_agents || []).map((ca: any) => ({
    id: ca.id,
    agentId: ca.agents?.id || ca.agent_id,
    name: ca.agents?.name || '',
    agency: ca.agents?.agency || '',
    email: ca.agents?.email || '',
    phone: ca.agents?.phone || '',
    isPrimary: ca.is_primary || false,
  })),
  prAvailable: dbGuest.pr_available,
  priority: dbGuest.priority || 'Unassigned',
  storyDetails: dbGuest.story_details || undefined,
  notes: dbGuest.notes || undefined,
  source: dbGuest.source
})

const convertAgent = (dbAgent: any): Agent => ({
  id: dbAgent.id,
  name: dbAgent.name,
  agency: dbAgent.agency,
  email: dbAgent.email,
  phone: dbAgent.phone,
  eventYear: dbAgent.event_year,
  totalRequests: dbAgent.total_requests,
  source: dbAgent.source
})

// Guest service
export const guestService = {
  async getAll(eventYear?: number): Promise<Guest[]> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - returning empty guests array')
      return []
    }

    try {
      let query = supabase
        .from('celebrities')
        .select('*, celebrity_agents(id, is_primary, agent_id, agents(id, name, agency, email, phone))')
      if (eventYear) query = query.eq('event_year', eventYear)
      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) throw error
      return (data || []).map(convertGuest)
    } catch (error) {
      console.error('Error fetching guests:', error)
      if (error.message?.includes('Invalid API key')) {
        console.error('Supabase API key is invalid or expired. Please check your environment variables.')
      }
      return []
    }
  },

  async create(guest: Omit<Guest, 'id'>, eventYear?: number): Promise<Guest | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot save guest')
      return null
    }

    try {
      const insertData: any = {
          name: guest.name,
          category: guest.category,
          days: guest.days,
          known_for: guest.knownFor,
          agent: null,
          agent_contact: null,
          pr_available: guest.prAvailable,
          priority: guest.priority || 'Unassigned',
          story_details: guest.storyDetails || null,
          notes: guest.notes || null,
          source: guest.source || 'manual'
      }
      if (eventYear) insertData.event_year = eventYear
      const { data, error } = await supabase
        .from('celebrities')
        .insert(insertData)
        .select()
        .single()

      if (error) throw error
      if (!data) return null

      // Create agent assignments if provided
      if (guest.agents && guest.agents.length > 0) {
        for (const agentAssignment of guest.agents) {
          await supabase.from('celebrity_agents').insert({
            celebrity_id: data.id,
            agent_id: agentAssignment.agentId,
            is_primary: agentAssignment.isPrimary,
            event_year: eventYear || new Date().getFullYear()
          })
        }
      }

      // Re-fetch with junction data
      const { data: fullData } = await supabase
        .from('celebrities')
        .select('*, celebrity_agents(id, is_primary, agent_id, agents(id, name, agency, email, phone))')
        .eq('id', data.id)
        .single()

      return fullData ? convertGuest(fullData) : convertGuest(data)
    } catch (error) {
      console.error('Error creating guest:', error)
      throw error
    }
  },

  async update(id: string, updates: Partial<Omit<Guest, 'id'>>): Promise<Guest | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot update guest')
      return null
    }

    try {
      const dbUpdates: any = {}

      if (updates.name !== undefined) dbUpdates.name = updates.name
      if (updates.category !== undefined) dbUpdates.category = updates.category
      if (updates.days !== undefined) dbUpdates.days = updates.days
      if (updates.knownFor !== undefined) dbUpdates.known_for = updates.knownFor
      if (updates.prAvailable !== undefined) dbUpdates.pr_available = updates.prAvailable
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority
      if (updates.storyDetails !== undefined) dbUpdates.story_details = updates.storyDetails
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes
      if (updates.source !== undefined) dbUpdates.source = updates.source

      if (Object.keys(dbUpdates).length > 0) {
        const { error } = await supabase
          .from('celebrities')
          .update(dbUpdates)
          .eq('id', id)

        if (error) throw error
      }

      // Re-fetch with junction data
      const { data, error: fetchError } = await supabase
        .from('celebrities')
        .select('*, celebrity_agents(id, is_primary, agent_id, agents(id, name, agency, email, phone))')
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError
      return data ? convertGuest(data) : null
    } catch (error) {
      console.error('Error updating guest:', error)
      throw error
    }
  },

  async bulkCreate(guests: Omit<Guest, 'id'>[], eventYear?: number): Promise<Guest[]> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot save guests')
      return []
    }

    try {
      const year = eventYear || new Date().getFullYear()
      const dbGuests = guests.map(guest => {
        const row: any = {
          name: guest.name,
          category: guest.category,
          days: guest.days,
          known_for: guest.knownFor,
          agent: null,
          agent_contact: null,
          pr_available: guest.prAvailable,
          priority: guest.priority || 'Unassigned',
          story_details: guest.storyDetails || null,
          notes: guest.notes || null,
          source: guest.source || 'bulk_upload'
        }
        if (eventYear) row.event_year = eventYear
        return row
      })

      const { data, error } = await supabase
        .from('celebrities')
        .insert(dbGuests)
        .select()

      if (error) throw error
      const createdGuests = data || []

      // Create agent assignments from the guest.agents arrays
      const junctionInserts: any[] = []
      for (let i = 0; i < guests.length; i++) {
        const guest = guests[i]
        const created = createdGuests[i]
        if (created && guest.agents && guest.agents.length > 0) {
          for (const agentAssignment of guest.agents) {
            junctionInserts.push({
              celebrity_id: created.id,
              agent_id: agentAssignment.agentId,
              is_primary: agentAssignment.isPrimary,
              event_year: year
            })
          }
        }
      }

      if (junctionInserts.length > 0) {
        await supabase.from('celebrity_agents').insert(junctionInserts)
      }

      // Re-fetch all with junction data
      const ids = createdGuests.map(g => g.id)
      const { data: fullData } = await supabase
        .from('celebrities')
        .select('*, celebrity_agents(id, is_primary, agent_id, agents(id, name, agency, email, phone))')
        .in('id', ids)
        .order('created_at', { ascending: false })

      return (fullData || []).map(convertGuest)
    } catch (error) {
      console.error('Error bulk creating guests:', error)
      throw error
    }
  },

  async delete(id: string): Promise<boolean> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot delete guest')
      return false
    }

    try {
      const { error } = await supabase
        .from('celebrities')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting guest:', error)
      throw error
    }
  },

  async bulkDelete(ids: string[]): Promise<boolean> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot bulk delete guests')
      return false
    }

    try {
      const { error } = await supabase
        .from('celebrities')
        .delete()
        .in('id', ids)
      
      if (error) throw error
      return true
    } catch (error) {
      console.error('Error bulk deleting guests:', error)
      throw error
    }
  },

  async deleteAll(): Promise<boolean> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot delete all guests')
      return false
    }

    try {
      const { error } = await supabase
        .from('celebrities')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000') // Delete all except impossible ID
      
      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting all guests:', error)
      throw error
    }
  },

  async assignAgent(celebrityId: string, agentId: string, isPrimary: boolean, eventYear: number): Promise<void> {
    const { error } = await supabase.from('celebrity_agents').insert({
      celebrity_id: celebrityId,
      agent_id: agentId,
      is_primary: isPrimary,
      event_year: eventYear
    })
    if (error) throw error
  },

  async removeAgent(celebrityId: string, agentId: string, eventYear: number): Promise<void> {
    const { error } = await supabase.from('celebrity_agents')
      .delete()
      .eq('celebrity_id', celebrityId)
      .eq('agent_id', agentId)
      .eq('event_year', eventYear)
    if (error) throw error
  },

  async setAgents(celebrityId: string, agentAssignments: { agentId: string, isPrimary: boolean }[], eventYear: number): Promise<void> {
    // Delete existing assignments for this celebrity/year
    await supabase.from('celebrity_agents')
      .delete()
      .eq('celebrity_id', celebrityId)
      .eq('event_year', eventYear)

    // Insert new assignments
    if (agentAssignments.length > 0) {
      const { error } = await supabase.from('celebrity_agents').insert(
        agentAssignments.map(a => ({
          celebrity_id: celebrityId,
          agent_id: a.agentId,
          is_primary: a.isPrimary,
          event_year: eventYear
        }))
      )
      if (error) throw error
    }
  },

  async setPrimaryAgent(celebrityId: string, agentId: string, eventYear: number): Promise<void> {
    // Unset all primary flags for this celebrity
    await supabase.from('celebrity_agents')
      .update({ is_primary: false })
      .eq('celebrity_id', celebrityId)
      .eq('event_year', eventYear)

    // Set the new primary
    await supabase.from('celebrity_agents')
      .update({ is_primary: true })
      .eq('celebrity_id', celebrityId)
      .eq('agent_id', agentId)
      .eq('event_year', eventYear)
  }
}

// Agent service
export const agentService = {
  async getAll(eventYear?: number): Promise<Agent[]> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - returning empty agents array')
      return []
    }

    try {
      let query = supabase
        .from('agents')
        .select('*')
      if (eventYear) query = query.eq('event_year', eventYear)
      const { data, error } = await query.order('created_at', { ascending: false })
      
      if (error) throw error
      return (data || []).map(convertAgent)
    } catch (error) {
      console.error('Error fetching agents:', error)
      return []
    }
  },

  async create(agent: Omit<Agent, 'id'>, eventYear?: number): Promise<Agent | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot save agent')
      return null
    }

    try {
      const row: any = {
        name: agent.name,
        agency: agent.agency,
        email: agent.email,
        phone: agent.phone,
        total_requests: 0,
        source: agent.source || 'manual'
      }
      if (eventYear) row.event_year = eventYear

      const { data, error } = await supabase
        .from('agents')
        .insert(row)
        .select()
        .single()

      if (error) throw error
      return data ? convertAgent(data) : null
    } catch (error) {
      console.error('Error creating agent:', error)
      throw error
    }
  },

  async bulkCreate(agents: Omit<Agent, 'id'>[], eventYear?: number): Promise<Agent[]> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot save agents')
      return []
    }

    try {
      const dbAgents = agents.map(agent => {
        const row: any = {
          name: agent.name,
          agency: agent.agency,
          email: agent.email,
          phone: agent.phone,
          total_requests: agent.totalRequests,
          source: agent.source || 'bulk_upload'
        }
        if (eventYear) row.event_year = eventYear
        return row
      })

      const { data, error } = await supabase
        .from('agents')
        .insert(dbAgents)
        .select()
      
      if (error) throw error
      return (data || []).map(convertAgent)
    } catch (error) {
      console.error('Error bulk creating agents:', error)
      throw error
    }
  },

  async getByEmail(email: string): Promise<Agent | null> {
    if (!hasValidCredentials) {
      return null
    }

    try {
      const { data, error } = await supabase
        .from('agents')
        .select('*')
        .eq('email', email)
        .single()
      
      if (error && error.code !== 'PGRST116') throw error // PGRST116 is "not found"
      return data ? convertAgent(data) : null
    } catch (error) {
      console.error('Error finding agent by email:', error)
      return null
    }
  },

  async deleteAll(): Promise<boolean> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot delete all agents')
      return false
    }

    try {
      console.log('agentService.deleteAll: Starting BRUTAL deletion process')
      
      // Method 1: Use admin client to bypass RLS
      console.log('agentService.deleteAll: Using admin client to bypass RLS')
      console.log('agentService.deleteAll: supabaseAdmin available:', !!supabaseAdmin)
      
      if (!supabaseAdmin) {
        console.error('Admin client not available - cannot delete agents')
        throw new Error('Admin client not available - cannot delete agents')
      }
      
      // First check what's in the table with admin client
      const { data: allAgentsAdmin, error: fetchAdminError } = await supabaseAdmin
        .from('agents')
        .select('id, name')
      
      console.log('agentService.deleteAll: Admin client sees agents:', allAgentsAdmin?.length, allAgentsAdmin)
      
      // Force delete with admin client (bypasses RLS)  
      const { data: sqlResult, error: sqlError } = await supabaseAdmin
        .from('agents')
        .delete({ count: 'exact' })
        .neq('id', '00000000-0000-0000-0000-000000000000')
      
      if (!sqlError) {
        console.log('agentService.deleteAll: SQL delete result:', sqlResult)
      } else {
        console.log('agentService.deleteAll: SQL delete failed, trying regular method')
        
        // Method 2: Get all IDs and delete individually
        const { data: allAgents, error: fetchError } = await supabase
          .from('agents')
          .select('id')
          
        if (!fetchError && allAgents) {
          console.log('agentService.deleteAll: Found', allAgents.length, 'agents to delete')
          
          for (const agent of allAgents) {
            const { error: deleteError } = await supabase
              .from('agents')
              .delete()
              .eq('id', agent.id)
            
            if (deleteError) {
              console.warn('agentService.deleteAll: Failed to delete agent', agent.id, deleteError)
            }
          }
        }
      }
      
      // Verify final count
      const { count: finalCount } = await supabase
        .from('agents')
        .select('*', { count: 'exact', head: true })
      
      console.log('agentService.deleteAll: Final agent count:', finalCount)
      
      return true
    } catch (error) {
      console.error('agentService.deleteAll: Error in brutal deletion:', error)
      throw error
    }
  },

  async update(id: string, updates: Partial<Omit<Agent, 'id'>>): Promise<Agent | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot update agent')
      return null
    }

    try {
      console.log('Agent service update called with:', { id, updates })
      
      const dbUpdates: any = {}
      
      if (updates.name !== undefined) dbUpdates.name = updates.name
      if (updates.agency !== undefined) dbUpdates.agency = updates.agency
      if (updates.email !== undefined) dbUpdates.email = updates.email
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone
      if (updates.eventYear !== undefined) dbUpdates.event_year = updates.eventYear
      if (updates.totalRequests !== undefined) dbUpdates.total_requests = updates.totalRequests
      if (updates.source !== undefined) dbUpdates.source = updates.source

      console.log('Database updates object:', dbUpdates)

      const { data, error } = await supabase
        .from('agents')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) {
        console.error('Database error in agent service:', error)
        throw error
      }
      
      console.log('Database update result:', data)
      return data ? convertAgent(data) : null
    } catch (error) {
      console.error('Error updating agent:', error)
      throw error
    }
  },

  async delete(id: string): Promise<boolean> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot delete agent')
      return false
    }

    try {
      console.log('agentService.delete: Attempting to delete agent with ID:', id)
      const { data, error } = await supabase
        .from('agents')
        .delete()
        .eq('id', id)
        .select()
      
      console.log('agentService.delete: Supabase response:', { data, error })
      
      if (error) {
        console.error('agentService.delete: Supabase error:', error)
        throw error
      }
      
      console.log('agentService.delete: Successfully deleted from database')
      return true
    } catch (error) {
      console.error('agentService.delete: Error deleting agent:', error)
      throw error
    }
  }
}